import { useEffect, useRef, useState } from 'react';

/**
 * The tenant's effective Solution Bank categories — ONE authority, every consumer.
 *
 * Solution Bank used to carry its own list:
 *
 *     const categories = ['all','Technical Support','Billing','Product Issues','Account Management'];
 *
 * Nothing produced that list but this file. Meanwhile the review modal and the backend approval gate
 * both resolved TaxonomyService.resolve_effective_categories, so every solution authored through the
 * Create/Edit dialogs carried a category the approval gate was guaranteed to reject with a 422 — you
 * could write knowledge the platform could never approve, and the picker offered no way out.
 *
 * This hook is the only place the Business Web Solution Bank learns what a category is.
 *
 * TENANT SCOPE. `businessId` is the authenticated business from BusinessContext, and the endpoint
 * re-authorizes it server-side (assert_category_tenant → 403 cross-tenant, with no existence leak).
 * The list is keyed to that id and re-fetched when it changes, so switching tenant can never leave
 * another tenant's categories on screen.
 *
 * ENABLED ONLY. New authoring must not offer a category the approval gate will refuse; the gate now
 * resolves the same enabled set.
 *
 * NO FALLBACK. When a tenant has configured nothing, this returns an empty list and the caller must
 * say so. A legacy fallback here would quietly restore the second authority this hook exists to
 * delete — an empty picker that tells the truth is better than a full one that lies.
 */
export function useTenantCategories(businessId, makeAuthenticatedRequest) {
  const [categories, setCategories] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(null);

  // `makeAuthenticatedRequest` is rebuilt on every render of its provider, so depending on its
  // IDENTITY re-ran this effect on every render: fetch -> setState -> render -> fetch, an infinite
  // loop that also hammered the endpoint. React's exhaustive-deps rule wants it in the array; the
  // rule is about staleness, and a ref answers that without making identity a trigger. The tenant is
  // the only thing that should cause a refetch.
  const requestRef = useRef(makeAuthenticatedRequest);
  requestRef.current = makeAuthenticatedRequest;

  useEffect(() => {
    let alive = true;
    if (!businessId) { setCategories([]); setLoaded(false); return undefined; }
    setLoaded(false);
    setError(null);
    // Clear immediately: on a tenant switch the previous tenant's list must never be briefly visible.
    setCategories([]);
    (async () => {
      try {
        const r = await requestRef.current(`/api/v1/business/${businessId}/feedback-categories`);
        if (!alive) return;
        if (!r.ok) { setError(`HTTP ${r.status}`); setLoaded(true); return; }
        const d = await r.json();
        const raw = Array.isArray(d) ? d
          : Array.isArray(d.categories) ? d.categories
          : Array.isArray(d.feedback_categories) ? d.feedback_categories
          : [];
        // The stored name is sent back verbatim on save — the approval gate compares against it, so
        // trimming or prettifying here would be this component inventing a category name.
        const names = raw
          .filter((c) => c && c.is_enabled !== false && c.name)
          .map((c) => String(c.name));
        setCategories(Array.from(new Set(names)));
        setLoaded(true);
      } catch (e) {
        if (!alive) return;
        setError(e?.message || 'failed');
        setLoaded(true);
      }
    })();
    return () => { alive = false; };
  }, [businessId]);   // tenant only — see the ref note above

  return { categories, loaded, error };
}

export default useTenantCategories;
